import rootStore from './root'
import { Error as SNotifyError } from '@/plugins/snotify'
import feathers from '@feathersjs/client'
import io from 'socket.io-client'
import PQueue from 'p-queue'
import i18n from '@/i18n'

i18n.mergeLocaleMessage('en', {
  'changes failed': 'Changes failed',
  'storage limit reached': 'Storage space exceeded.'
})

i18n.mergeLocaleMessage('de', {
  'changes failed': 'Änderungen fehlgeschlagen.',
  'storage limit reached': 'Maximale Datenmenge erreicht.'
})

const client = feathers()
const socket = io()
client.configure(feathers.socketio(socket))
client.configure(feathers.authentication())
socket.on('disconnect', (reason) => {
  if (reason === 'io server disconnect') {
    rootStore.dispatch('setProject', { project: undefined })
  }
  // else the socket will automatically try to reconnect
})

function getProjectService () {
  return new Promise((resolve, reject) => {
    const cookie = document.cookie.match(new RegExp('(^| )touchwert-jwt=([^;]+)'))
    if (!cookie) {
      return reject(new Error('Could not find cookie.'))
    }
    client.authenticate({
      strategy: 'jwt',
      accessToken: cookie[2]
    }).then(() => {
      resolve(client.service('projects'))
    }).catch((e) => {
      reject(new Error('Could not authenticate with websocket.'))
    })
  })
}

function uuid () {
  let d = new Date().getTime()
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    d += performance.now()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (d + Math.random() * 16) % 16 | 0
    d = Math.floor(d / 16)
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
  })
}

const currentSession = uuid()

let service

export default store => {
  store.loadProject = async project => {
    if (!service) {
      service = await getProjectService()
      service.on('mutation', ({ oldVersion, newVersion, session, mutation }) => {
        // only update local state, if the mutation updates the current version. This session should already be on newVersion
        if (oldVersion === store.state._version) {
          mutation.payload._replayed = true
          store.commit(mutation.type, mutation.payload)
          store.commit('setVersion', { version: newVersion })
          store.dispatch('manageModules', {}) // for addChild and removeChild calls
        } else if (session !== currentSession) { // we probably missed some mutations - reset
          store.loadProject(project)
        }
      })
      const queue = new PQueue({ concurrency: 1 }) // run the updates in a queue to get versioning correct
      store.subscribe((mutation, state) => {
        if (!mutation.payload._replayed && mutation.type !== 'setVersion') {
          queue.add(async () => {
            // hold old version
            const oldVersion = store.state._version
            // create new version
            store.commit('setVersion', { version: uuid() })
            await service.update(
              state._id,
              {
                mutation,
                session: currentSession,
                oldVersion: oldVersion,
                newVersion: state._version
              }
            ).catch(e => {
              store.loadProject(project)
              if (e.code === 402) {
                new SNotifyError(i18n.t('storage limit reached')).open()
              } else {
                new SNotifyError(i18n.t('changes failed')).open()
              }
            })
          })
        }
      })
    }
    await service.get(project).then(async data => {
      await rootStore.dispatch('setEditAllowed', { allowed: data._editAllowed })
      await store.dispatch('replaceState', { newState: data })
      await rootStore.dispatch('setProject', { project })
    })
  }
}
